import type { CloseReason, PositionType } from '../types';

/**
 * Leverage-aware PnL math, shared by the live readout, the close-result
 * visuals, the demo-mode balance accounting, and the chart progress bar.
 *
 * For an isolated-margin perp the user's return is on their *collateral*, not
 * the raw price move:
 *
 *   priceMove% = (mark - entry) / entry * dir      (dir = +1 long, -1 short)
 *   ROI%       = priceMove% * leverage             (return on collateral)
 *   PnL$       = collateral * priceMove% * leverage
 *
 * `leverage` defaults to 1 so any caller that hasn't captured it yet degrades
 * to the unleveraged price move rather than throwing.
 */

export interface PnlResult {
    /** Return on collateral, in percent (e.g. +30 = +30% of margin). */
    pnlPercent: number;
    /** Profit/loss in USD (collateral currency). */
    pnlValue: number;
}

/**
 * ROE as a percentage, from the exchange's own figures.
 *
 * THE single derivation for an open position's PnL percentage. Prefers
 * Hyperliquid's `returnOnEquity`; falls back to pnl/margin using the exchange's
 * marginUsed, never the collateral the client requested and never a client-side
 * leverage assumption. Null when the exchange reports neither.
 *
 * Lived in three copies (the store selector, useLivePnL, the share card), which
 * is how surfaces drifted apart. Anything showing an open position's percentage
 * calls this.
 */
export function exchangePnlPercent(position: {
    returnOnEquity: number | null;
    unrealizedPnl: number | null;
    marginUsed: number | null;
}): number | null {
    const { returnOnEquity, unrealizedPnl, marginUsed } = position;
    if (returnOnEquity !== null) return returnOnEquity * 100;
    if (unrealizedPnl !== null && marginUsed !== null && marginUsed > 0) {
        return (unrealizedPnl / marginUsed) * 100;
    }
    return null;
}

/**
 * The exchange's PnL plus the price move since it was taken.
 *
 * THE single way to show an open position's PnL between polls. The exchange is
 * authoritative but only answers every few seconds, so the figure would step
 * rather than track the market. This anchors on the exchange's number and adds
 * only what the price has done SINCE that snapshot:
 *
 *   pnl(t) = exchangePnl + direction × (livePrice(t) − priceAtSnapshot) × size
 *
 * Why a delta and not a straight recompute from the exchange's entry and size:
 * Hyperliquid prices PnL off MARK, an oracle blend, while the feed carries LAST.
 * On the reported BTC trade they sat $20.5 apart — recomputing from last would
 * have read +$0.36 against the exchange's +$0.27, a 33% error, which is the very
 * mismatch this whole chain of fixes exists to remove. Anchoring bakes that basis
 * into the snapshot so it never resurfaces, the displayed value equals the
 * exchange EXACTLY at every poll, and any drift is corrected on the next one
 * rather than accumulating.
 */
export interface PnlAnchor {
    /** the exchange's unrealized PnL at snapshot time */
    pnlValue: number;
    /** its percentage at the same instant, so the two stay in lockstep */
    pnlPercent: number | null;
    /** the feed price when the snapshot was taken */
    anchorPrice: number | null;
    /** exchange position size, in asset units */
    size: number | null;
    isLong: boolean;
    /** exchange marginUsed — never the collateral the client requested */
    marginUsed: number | null;
}

export function realtimePnl(anchor: PnlAnchor, livePrice: number): PnlResult {
    const canExtrapolate =
        anchor.anchorPrice !== null &&
        anchor.size !== null &&
        Number.isFinite(livePrice) &&
        livePrice > 0;
    if (!canExtrapolate) {
        return {
            pnlValue: anchor.pnlValue,
            pnlPercent: anchor.pnlPercent ?? 0,
        };
    }
    const dir = anchor.isLong ? 1 : -1;
    const delta = dir * (livePrice - anchor.anchorPrice!) * anchor.size!;
    const pnlValue = anchor.pnlValue + delta;
    // the percentage moves by the same delta over the exchange's margin, so it
    // equals the exchange's ROE exactly at the anchor.
    const pnlPercent =
        anchor.marginUsed !== null && anchor.marginUsed > 0
            ? (anchor.pnlPercent ?? 0) + (delta / anchor.marginUsed) * 100
            : (anchor.pnlPercent ?? 0);
    return { pnlValue, pnlPercent };
}

/** Signed fractional price move relative to entry (long: +up, short: +down). */
export function priceMoveFraction(
    entry: number,
    mark: number,
    posType: PositionType,
): number {
    if (!Number.isFinite(entry) || entry === 0) return 0;
    const dir = posType === 'LONG' ? 1 : -1;
    return ((mark - entry) / entry) * dir;
}

export function computePnl(
    entry: number,
    mark: number,
    posType: PositionType,
    collateral: number,
    leverage = 1,
    closeReason?: CloseReason,
): PnlResult {
    const lev = Number.isFinite(leverage) && leverage > 0 ? leverage : 1;
    const move = priceMoveFraction(entry, mark, posType);

    let pnlPercent = move * lev * 100;
    let pnlValue = collateral * move * lev;

    // Terminal-reason overrides keep the displayed outcome consistent with why
    // the backend closed the trade (the exit tick we sampled can lag slightly).
    if (closeReason === 'TakeProfit' && pnlValue < 0) {
        pnlPercent = Math.abs(pnlPercent);
        pnlValue = Math.abs(pnlValue);
    } else if (closeReason === 'StopLoss' && pnlValue > 0) {
        pnlPercent = -Math.abs(pnlPercent);
        pnlValue = -Math.abs(pnlValue);
    } else if (closeReason === 'Liquidated') {
        // Liquidation wipes the collateral.
        pnlPercent = -100;
        pnlValue = -collateral;
    }

    return { pnlPercent, pnlValue };
}

/**
 * Convert a take-profit / stop-loss target expressed as ROI % (gain or loss on
 * collateral) into basis points of *price* move, which is what the backend's
 * `takeProfitBps` / `stopLossBps` expect (`markPx * (1 ± bps/10000)`).
 *
 *   priceBps = roi% * 100 / leverage
 *
 * e.g. 100% ROI at 30x → 333 bps price move.
 */
export function roiPercentToPriceBps(
    roiPercent: number,
    leverage: number,
): number {
    const lev = Number.isFinite(leverage) && leverage > 0 ? leverage : 1;
    if (!Number.isFinite(roiPercent) || roiPercent <= 0) return 0;
    return Math.max(1, Math.round((roiPercent * 100) / lev));
}

/**
 * A trigger that sits BELOW mark (a short's take profit, a long's stop loss)
 * can only ride the price down to zero, i.e. at most 100% of price. At
 * 10000 bps the backend's `markPx * (1 - bps/10000)` is exactly $0, which
 * Hyperliquid rejects; above it the price goes negative.
 *
 * Longs never hit this on take profit (the price can rise without bound),
 * and shorts never hit it on stop loss for the same reason.
 */
export const MAX_DOWNSIDE_TRIGGER_BPS = 10_000;

/**
 * Whether a take-profit target (ROI % on collateral) is reachable for the
 * given side at this leverage. On a short the ROI cannot exceed
 * `leverage * 100%` (the price reaching $0), so e.g. 300% at 3x is long only.
 */
export function isTakeProfitReachable(
    roiPercent: number,
    leverage: number,
    posType: PositionType,
): boolean {
    const bps = roiPercentToPriceBps(roiPercent, leverage);
    if (bps <= 0) return true;
    return posType === 'LONG' || bps < MAX_DOWNSIDE_TRIGGER_BPS;
}

/** Equal is NOT reachable: a stop exactly at liquidation never gets to fire. */
const REACHABLE_EPSILON = 1e-9;

/**
 * The fraction the price has to move against an isolated position before the
 * exchange liquidates it, from the same margin math as
 * {@link estimateLiquidationPrice}:
 *
 *   long  liq = entry * (1 - 1/lev) / (1 - mm)  →  move = 1 - (1 - 1/lev)/(1 - mm)
 *   short liq = entry * (1 + 1/lev) / (1 + mm)  →  move = (1 + 1/lev)/(1 + mm) - 1
 *
 * At `mm = 0` (margin terms unknown) both sides collapse to the naive `1/lev`.
 * The maintenance margin makes the SHORT side the tighter one — on a 3x market
 * (mm = 1/6) a short liquidates on a 14.3% move where a long survives to 20% —
 * which is why the reported PONS stop sat behind liquidation on a short and
 * would have been fine on a long.
 */
export function liquidationMoveFraction(
    leverage: number,
    posType: PositionType,
    maintenanceRate: number,
): number {
    const lev = Number.isFinite(leverage) && leverage > 0 ? leverage : 1;
    // A rate outside [0, 1) would flip the divisor's sign; treat it as unknown.
    const mm =
        Number.isFinite(maintenanceRate) &&
        maintenanceRate > 0 &&
        maintenanceRate < 1
            ? maintenanceRate
            : 0;
    const frac = 1 / lev;
    return posType === 'LONG'
        ? 1 - (1 - frac) / (1 - mm)
        : (1 + frac) / (1 + mm) - 1;
}

/**
 * Whether a stop-loss target (ROI % on collateral) can still fire for the given
 * side at this leverage — i.e. whether the price reaches the stop BEFORE it
 * reaches liquidation.
 *
 * This used to ask only whether the trigger ran out of price ($0 on a long),
 * which let a stop sit BEHIND the liquidation price and read as armed
 * protection that can never fire. That is the reported PONS liquidation: a 50%
 * ROI stop on a 3x SHORT of a max-leverage-3 market is a 16.7% move, and the
 * position liquidates at 14.3%. The backend now rejects such a trigger
 * (`triggerUnreachable`); this is the same test, so the form refuses it before
 * anything is sent.
 *
 * `maintenanceRate` is the market's maintenance-margin fraction (resolve it
 * with `getMaintenanceRate`). It defaults to 0 = unknown, where the test
 * degrades to the naive liquidation point, `roi < 100%`, on both sides — no
 * preset the form offers (25/50/75%) is affected by the difference.
 */
export function isStopLossReachable(
    roiPercent: number,
    leverage: number,
    posType: PositionType,
    maintenanceRate = 0,
): boolean {
    const lev = Number.isFinite(leverage) && leverage > 0 ? leverage : 1;
    if (!Number.isFinite(roiPercent) || roiPercent <= 0) return true;
    const stopMove = roiPercent / 100 / lev;
    const liqMove = liquidationMoveFraction(lev, posType, maintenanceRate);
    return stopMove < liqMove - REACHABLE_EPSILON;
}

/**
 * Estimated isolated-margin liquidation price, for the pre-trade preview and
 * for the window before `GET /speed/position/:coin` reports the real one.
 *
 * Hyperliquid liquidates when equity falls to the MAINTENANCE margin, not to
 * zero. At the liquidation price
 *
 *   margin + size * (liq - entry) = size * liq * maintenanceRate    (long)
 *
 * which solves to
 *
 *   long  liq = entry * (1 - 1/leverage) / (1 - maintenanceRate)
 *   short liq = entry * (1 + 1/leverage) / (1 + maintenanceRate)
 *
 * `maintenanceRate` is that maintenance-margin fraction — `liqLostP / 1e4`
 * from the market's leverage tier, i.e. half the initial rate at the tier's
 * max leverage. Resolve it with `getMaintenanceRate`; 0 means "unknown" and
 * degrades to the naive `entry * (1 ∓ 1/leverage)`.
 *
 * That naive form is what this used to return unconditionally, and it is
 * systematically optimistic: on SILVER at 15x it drew liquidation 6.7% away
 * when the exchange put it at 4.8% (63.7 -> 59.45 shown, 60.67 real), and at a
 * market's max leverage it overstates the surviving move by about 2x. Fees and
 * the fill-vs-mark gap still move the real trigger a little closer — this is an
 * estimate — but no longer in a way that flatters the position.
 */
export function estimateLiquidationPrice(
    entry: number,
    posType: PositionType,
    leverage: number,
    maintenanceRate: number,
): number | null {
    const lev = Number.isFinite(leverage) && leverage > 0 ? leverage : 1;
    if (!Number.isFinite(entry) || entry <= 0) return null;
    // A rate outside [0, 1) would flip the divisor's sign; treat it as unknown.
    const mr =
        Number.isFinite(maintenanceRate) &&
        maintenanceRate > 0 &&
        maintenanceRate < 1
            ? maintenanceRate
            : 0;
    const frac = 1 / lev;
    const liq =
        posType === 'LONG'
            ? (entry * (1 - frac)) / (1 - mr)
            : (entry * (1 + frac)) / (1 + mr);
    return liq > 0 ? liq : 0;
}
